/**
 * Items (price list) helpers: form state, validation, request building, and
 * GL account filtering. Validation mirrors the backend ItemBusinessLayer /
 * ItemService so most mistakes are caught before a round-trip; the backend
 * stays authoritative.
 */

import * as types from '../api/types';

export const ITEM_TYPE_OPTIONS: types.ItemType[] = [
  types.ItemType.SERVICE,
  types.ItemType.LABOR,
  types.ItemType.PRODUCT,
  types.ItemType.FEE,
];

export const ITEM_TYPE_LABELS: Record<types.ItemType, string> = {
  [types.ItemType.SERVICE]: 'Service',
  [types.ItemType.LABOR]: 'Labor',
  [types.ItemType.PRODUCT]: 'Product',
  [types.ItemType.FEE]: 'Fee',
};

export const ITEM_NAME_MAX_LENGTH = 255;
export const DEFAULT_UNIT_OF_MEASURE = 'each';

/** Non-negative with at most 2 decimals: the price and cost columns are Decimal(14,2). */
const MONEY_PATTERN = /^\d+(\.\d{1,2})?$/;
const MONEY_MAX = 1e12;

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** "150.5" -> "$150.50"; non-numeric input is returned unchanged. */
export function formatMoney(value: string | number): string {
  const n = Number(value);
  return Number.isFinite(n) ? currencyFormatter.format(n) : String(value);
}

/** "12.50" -> "12.5" for display, keeping non-numeric input unchanged. */
export function formatQuantity(value: string | number): string {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value);
}

/** Item revenue accounts: postable REVENUE (backend validateAccountTypesForItemType + postable check). */
export function isItemRevenueAccount(account: types.GLAccount): boolean {
  return account.accountType === types.GLAccountType.REVENUE && account.isPostable;
}

/** Item expense (COGS) accounts: postable EXPENSE. */
export function isItemExpenseAccount(account: types.GLAccount): boolean {
  return account.accountType === types.GLAccountType.EXPENSE && account.isPostable;
}

/** Only PRODUCT items carry stock, so only they may have a reorder point (backend rule). */
export function allowsReorderPoint(itemType: types.ItemType): boolean {
  return itemType === types.ItemType.PRODUCT;
}

/** Only SERVICE and LABOR items take time on site, so only they carry a typical duration (tracker item 40). */
export function allowsDuration(itemType: types.ItemType): boolean {
  return itemType === types.ItemType.SERVICE || itemType === types.ItemType.LABOR;
}

/** Only SERVICE and LABOR items can be offered on the customer portal's request form (tracker item 45). */
export function allowsPortalRequest(itemType: types.ItemType): boolean {
  return itemType === types.ItemType.SERVICE || itemType === types.ItemType.LABOR;
}

/** The backend requires an expense account for PRODUCT items. */
export function requiresExpenseAccount(itemType: types.ItemType): boolean {
  return itemType === types.ItemType.PRODUCT;
}

/**
 * COGS posts on invoice only for PRODUCT items with both a unit cost and an
 * expense account (backend InvoiceBusinessLayer line COGS).
 */
export function postsCogs(form: Pick<ItemFormState, 'itemType' | 'unitCost' | 'expenseAccountId'>): boolean {
  return (
    form.itemType === types.ItemType.PRODUCT &&
    form.unitCost.trim() !== '' &&
    Number(form.unitCost) > 0 &&
    form.expenseAccountId !== ''
  );
}

export interface ItemFormState {
  name: string;
  itemType: types.ItemType;
  description: string;
  unitPrice: string;
  /** '' means no cost. */
  unitCost: string;
  unitOfMeasure: string;
  taxable: boolean;
  /** '' means no category. */
  categoryId: string;
  revenueAccountId: string;
  /** '' means no expense account. */
  expenseAccountId: string;
  /** '' means no price chart (priced at unitPrice). */
  priceChartId: string;
  /** '' means no default dispatch route. */
  routeId: string;
  /** '' means no checklist template (tracker item 44). */
  checklistTemplateId: string;
  /** Low-stock threshold, PRODUCT only; '' means none. Ignored (sent as cleared) for other types. */
  reorderPoint: string;
  /** Typical minutes per unit, SERVICE / LABOR only; '' means none. Ignored (sent as cleared) for other types. */
  defaultDurationMinutes: string;
  /** Offered on the customer portal's request form, SERVICE / LABOR only. Ignored (sent as false) for other types. */
  portalRequestable: boolean;
}

export const EMPTY_ITEM_FORM: ItemFormState = {
  name: '',
  itemType: types.ItemType.SERVICE,
  description: '',
  unitPrice: '',
  unitCost: '',
  unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
  taxable: true,
  categoryId: '',
  revenueAccountId: '',
  expenseAccountId: '',
  priceChartId: '',
  routeId: '',
  checklistTemplateId: '',
  reorderPoint: '',
  defaultDurationMinutes: '',
  portalRequestable: false,
};

/** Decimal string from the API -> the form's display value ("150.00" stays "150.00"). */
function decimalToInput(value: string | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

export function itemFormFromItem(item: types.Item): ItemFormState {
  return {
    name: item.name,
    itemType: item.itemType,
    description: item.description ?? '',
    unitPrice: decimalToInput(item.unitPrice),
    unitCost: decimalToInput(item.unitCost),
    unitOfMeasure: item.unitOfMeasure,
    taxable: item.taxable,
    categoryId: item.categoryId ?? item.category?.id ?? '',
    revenueAccountId: item.revenueAccount.id,
    expenseAccountId: item.expenseAccount?.id ?? '',
    priceChartId: item.priceChartId ?? '',
    routeId: item.routeId ?? '',
    checklistTemplateId: item.checklistTemplateId ?? '',
    reorderPoint: decimalToInput(item.reorderPoint),
    defaultDurationMinutes: item.defaultDurationMinutes ? String(item.defaultDurationMinutes) : '',
    portalRequestable: item.portalRequestable ?? false,
  };
}

function isValidMoney(text: string): boolean {
  return MONEY_PATTERN.test(text) && Number(text) < MONEY_MAX;
}

/** Returns an error message, or null when the form is valid. */
export function validateItemForm(form: ItemFormState): string | null {
  const name = form.name.trim();
  if (!name) return 'Name is required';
  if (name.length > ITEM_NAME_MAX_LENGTH) return `Name must be ${ITEM_NAME_MAX_LENGTH} characters or less`;
  if (!ITEM_TYPE_OPTIONS.includes(form.itemType)) return 'Select an item type';

  const unitPrice = form.unitPrice.trim();
  if (!unitPrice) return 'Unit price is required';
  if (!isValidMoney(unitPrice)) {
    return 'Unit price must be a non-negative amount with at most 2 decimal places';
  }

  const unitCost = form.unitCost.trim();
  if (unitCost && !isValidMoney(unitCost)) {
    return 'Unit cost must be a non-negative amount with at most 2 decimal places';
  }

  const reorderPoint = form.reorderPoint.trim();
  if (allowsReorderPoint(form.itemType) && reorderPoint && !isValidMoney(reorderPoint)) {
    return 'Reorder point must be a non-negative quantity with at most 2 decimal places';
  }

  const duration = form.defaultDurationMinutes.trim();
  if (allowsDuration(form.itemType) && duration) {
    const minutes = Number(duration);
    if (!/^\d+$/.test(duration) || minutes < 5 || minutes > 1440) {
      return 'Typical duration must be a whole number of minutes from 5 to 1440';
    }
  }

  if (!form.revenueAccountId) return 'Select a revenue account';
  if (requiresExpenseAccount(form.itemType) && !form.expenseAccountId) {
    return 'Select an expense account (required for products)';
  }
  return null;
}

/** The duration the form means: whole minutes for a SERVICE / LABOR item, else null (none). */
function effectiveDuration(form: Pick<ItemFormState, 'itemType' | 'defaultDurationMinutes'>): number | null {
  const text = form.defaultDurationMinutes.trim();
  return allowsDuration(form.itemType) && text ? Number(text) : null;
}

/** The reorder point the form means: the trimmed value for a PRODUCT, else '' (none). */
function effectiveReorderPoint(form: Pick<ItemFormState, 'itemType' | 'reorderPoint'>): string {
  return allowsReorderPoint(form.itemType) ? form.reorderPoint.trim() : '';
}

/** The portal flag the form means: the checkbox for a SERVICE / LABOR item, else false (an item turned into a product / fee is switched off). */
function effectivePortalRequestable(form: Pick<ItemFormState, 'itemType' | 'portalRequestable'>): boolean {
  return allowsPortalRequest(form.itemType) && form.portalRequestable;
}

export function buildCreateItemRequest(form: ItemFormState): types.CreateItemRequest {
  const description = form.description.trim();
  const unitCost = form.unitCost.trim();
  const reorderPoint = effectiveReorderPoint(form);
  const duration = effectiveDuration(form);
  return {
    name: form.name.trim(),
    itemType: form.itemType,
    unitPrice: form.unitPrice.trim(),
    unitOfMeasure: form.unitOfMeasure.trim() || DEFAULT_UNIT_OF_MEASURE,
    taxable: form.taxable,
    revenueAccountId: form.revenueAccountId,
    ...(description ? { description } : {}),
    ...(unitCost ? { unitCost } : {}),
    ...(form.expenseAccountId ? { expenseAccountId: form.expenseAccountId } : {}),
    ...(form.categoryId ? { categoryId: form.categoryId } : {}),
    ...(form.priceChartId ? { priceChartId: form.priceChartId } : {}),
    ...(form.routeId ? { routeId: form.routeId } : {}),
    ...(form.checklistTemplateId ? { checklistTemplateId: form.checklistTemplateId } : {}),
    ...(reorderPoint ? { reorderPoint } : {}),
    ...(duration ? { defaultDurationMinutes: duration } : {}),
    ...(effectivePortalRequestable(form) ? { portalRequestable: true } : {}),
  };
}

/** Numeric equality for decimal strings ("150" vs "150.00"). */
function sameAmount(a: string, b: string | null | undefined): boolean {
  if (b === null || b === undefined || b === '') return a === '';
  if (a === '') return false;
  return Number(a) === Number(b);
}

/**
 * PATCH body containing only the fields that changed. This matters beyond
 * tidiness: the backend refuses any update carrying revenueAccountId or
 * expenseAccountId for an item used on an invoice or scheduled job, even if
 * the value is unchanged. Cleared nullable fields are sent as null.
 */
export function buildUpdateItemRequest(form: ItemFormState, item: types.Item): types.UpdateItemRequest {
  const request: types.UpdateItemRequest = {};

  const name = form.name.trim();
  if (name !== item.name) request.name = name;

  if (form.itemType !== item.itemType) request.itemType = form.itemType;

  const description = form.description.trim();
  if (description !== (item.description ?? '')) request.description = description || null;

  const unitPrice = form.unitPrice.trim();
  if (!sameAmount(unitPrice, item.unitPrice)) request.unitPrice = unitPrice;

  const unitCost = form.unitCost.trim();
  if (!sameAmount(unitCost, item.unitCost)) request.unitCost = unitCost || null;

  const unitOfMeasure = form.unitOfMeasure.trim() || DEFAULT_UNIT_OF_MEASURE;
  if (unitOfMeasure !== item.unitOfMeasure) request.unitOfMeasure = unitOfMeasure;

  if (form.taxable !== item.taxable) request.taxable = form.taxable;

  const currentCategoryId = item.categoryId ?? item.category?.id ?? '';
  if (form.categoryId !== currentCategoryId) request.categoryId = form.categoryId || null;

  if (form.revenueAccountId !== item.revenueAccount.id) request.revenueAccountId = form.revenueAccountId;

  const currentExpenseId = item.expenseAccount?.id ?? '';
  if (form.expenseAccountId !== currentExpenseId) request.expenseAccountId = form.expenseAccountId || null;

  // Only when changed: the backend re-checks that a sent chart is active.
  if (form.priceChartId !== (item.priceChartId ?? '')) request.priceChartId = form.priceChartId || null;
  if (form.routeId !== (item.routeId ?? '')) request.routeId = form.routeId || null;
  // Only when changed: the backend re-checks that a newly linked template is active.
  if (form.checklistTemplateId !== (item.checklistTemplateId ?? '')) request.checklistTemplateId = form.checklistTemplateId || null;

  // A non-PRODUCT form means "no reorder point": switching a PRODUCT that has
  // one to another type sends null, which the backend requires.
  const reorderPoint = effectiveReorderPoint(form);
  if (!sameAmount(reorderPoint, item.reorderPoint)) request.reorderPoint = reorderPoint || null;

  // Likewise a duration: a PRODUCT / FEE form sends null to clear one.
  const duration = effectiveDuration(form);
  if (duration !== (item.defaultDurationMinutes ?? null)) request.defaultDurationMinutes = duration;

  // Likewise the portal flag: a PRODUCT / FEE form sends false, which the backend requires when the type changes
  const portalRequestable = effectivePortalRequestable(form);
  if (portalRequestable !== (item.portalRequestable ?? false)) request.portalRequestable = portalRequestable;

  return request;
}

/**
 * Fetch every page of a paginated list endpoint. The backend caps page size
 * at 100, so a single request would silently drop rows past the first page.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, limit: number) => Promise<types.PaginatedResponse<T>>,
  limit = 100,
  maxPages = 50
): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; page <= maxPages; page++) {
    const response = await fetchPage(page, limit);
    rows.push(...response.data);
    if (page >= (response.pagination?.pages ?? 1) || response.data.length === 0) break;
  }
  return rows;
}
